import { useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle, Truck } from '@phosphor-icons/react';
import {
  useCatenaConnection,
  formatTspName,
} from '../../../../../../hooks/useCatenaConnection';
import { useFeatureFlagEnabled } from '../../../../../../hooks/useFeatureFlag';

interface CatenaOnboardingStepProps {
  userId: string | undefined;
}

export default function CatenaOnboardingStep({ userId }: CatenaOnboardingStepProps) {
  const navigate = useNavigate();
  const catenaEnabled = useFeatureFlagEnabled('enable_catena_integration');
  // Read-only snapshot: acting happens on the integrations card, so skip the live lifecycle
  // entirely — no mount reconcile, no poll. Both are Catena round-trips on the dashboard's
  // critical path for a row whose only job is to link to the card.
  const { state, connectedTsps, loading } = useCatenaConnection(
    userId,
    catenaEnabled,
    false
  );

  if (!catenaEnabled) return null;

  const isConnected = state === 'connected';
  // Started but unfinished — point them at the step they still owe rather than inviting them to
  // start over from scratch.
  const isUnfinished = state === 'connecting';

  return (
    <div className="flex w-full flex-col gap-2 rounded-2xl border border-[#EAECF0] bg-white px-4 py-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#F2F4F7]">
          {isConnected ? (
            <CheckCircle size={20} weight="fill" className="text-[#12B76A]" />
          ) : (
            <Truck size={20} className="text-[#0083bf]" />
          )}
        </div>
        <div className="flex flex-col">
          <h3 className="text-[15px] font-semibold leading-tight text-primary">
            {isConnected
              ? 'ELD Connected'
              : isUnfinished
                ? 'Finish Connecting Your ELD'
                : 'Connect Your ELD'}
          </h3>
          <p className="text-[13px] leading-tight text-[#667085]">
            {isConnected
              ? 'Your telematics provider is linked.'
              : isUnfinished
                ? 'You started linking — one step left.'
                : 'Link your ELD (Motive, Samsara & more) to verify your mileage.'}
          </p>
        </div>
      </div>

      {loading ? (
        <p className="text-[13px] text-[#98A2B3] sm:flex-shrink-0">Checking…</p>
      ) : isConnected ? (
        connectedTsps.length > 0 && (
          <div className="flex flex-wrap gap-2 sm:flex-shrink-0">
            {connectedTsps.map((tsp) => (
              <span
                key={tsp}
                className="inline-flex items-center gap-1.5 rounded-lg border border-[#EAECF0] bg-[#F9FAFB] px-2.5 py-1 text-[13px] font-medium text-[#344054]"
              >
                <i className="h-1.5 w-1.5 rounded-full bg-[#0083bf]" />
                {formatTspName(tsp)}
              </span>
            ))}
          </div>
        )
      ) : (
        <button
          type="button"
          onClick={() => navigate('/dashboard/settings/integrations')}
          className="inline-flex w-full items-center justify-center gap-2 rounded-[10px] bg-[#101828] px-4 py-2 text-[13px] font-semibold text-white hover:bg-[#1D213B] sm:w-auto sm:flex-shrink-0"
        >
          {isUnfinished ? 'Finish connecting' : 'Connect ELD'}
          <ArrowRight size={16} />
        </button>
      )}
    </div>
  );
}
